import { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import {
  Alert,
  Card,
  Skeleton,
  MetricCard,
  MetricCardGrid,
  METRIC_READ_CAPTIONS,
  FilterBar,
  AreaChart,
  GroupedBarChart,
  DonutChart,
} from '@aaimplatform/ui';
import type {
  FilterDefinition,
  ActiveFilter,
} from '@aaimplatform/ui';
import type { components as AdminPortalUiComponents } from '@aaimplatform/institutional-types/generated/admin-portal-ui';
import { useLoanPipeline } from '../../hooks/useLoanPipeline';

/**
 * The four read outcomes, QueryReadOutcome in
 * schema-registry/admin-portal-ui.openapi.yaml.
 */
type ReadOutcome = AdminPortalUiComponents['schemas']['QueryReadOutcome'];

/**
 * GH #2932. The read outcome this page renders from, derived the way
 * ComplianceStatus.tsx derives it.
 *
 * TanStack v5 defines `isLoading` as `isPending && isFetching`, so a FIRST
 * fetch that is PAUSED (the browser offline before this page mounts) is
 * neither loading nor errored and holds no data. The page used to fall through
 * to the charts in that state and draw "Total 0" in the donut center and
 * footer for a read that never ran. That state is `unread` here, and no figure
 * is drawn from it.
 *
 * `failed` is an error with NO payload. A rejected background refetch over a
 * prior successful read keeps that payload, stays `read`, and is named in the
 * refresh-failed notice with the time of the read shown.
 */
function readOutcome(query: {
  isError: boolean;
  isLoading: boolean;
  data: unknown;
}): ReadOutcome {
  if (query.isError && query.data === undefined) return 'failed';
  if (query.isLoading) return 'loading';
  if (query.data !== undefined) return 'read';
  return 'unread';
}

/** The local clock time of a read, for the refresh-failed notice. */
function readTime(dataUpdatedAt: number): string {
  return new Date(dataUpdatedAt).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** A chart section whose read has not run: its title and the unread sentence. */
function UnreadChart({ title, subject }: { title: string; subject: string }) {
  return (
    <Card className="h-full">
      <div className="text-sm font-sans text-secondary">{title}</div>
      <div className="py-12 text-center text-tertiary">{`${subject} not yet read.`}</div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Filter Definitions
// ---------------------------------------------------------------------------

const FILTER_DEFINITIONS: FilterDefinition[] = [
  {
    id: 'stage',
    label: 'Pipeline Stage',
    type: 'select',
    options: [
      { value: 'submitted', label: 'Submitted' },
      { value: 'underwriting', label: 'Underwriting' },
      { value: 'approved', label: 'Approved' },
      { value: 'funded', label: 'Funded' },
    ],
  },
  {
    id: 'integration',
    label: 'Integration',
    type: 'select',
    options: [
      { value: 'cuanswers', label: 'CU*Answers' },
      { value: 'symitar', label: 'Symitar' },
      { value: 'corelation', label: 'Corelation' },
    ],
  },
  {
    id: 'dateRange',
    label: 'Date Range',
    type: 'daterange',
  },
];

// ---------------------------------------------------------------------------
// LoanPipelineDashboard
// ---------------------------------------------------------------------------

/**
 * LoanPipelineDashboard - Loan application flow and origination metrics
 *
 * Displays:
 * - Application volume (24h, 7d, 30d)
 * - Approval rates by integration
 * - Origination volume trends
 * - Pipeline stages (submitted, underwriting, approved, funded)
 * - Average time to funding
 *
 * Modernized with FilterBar for structured filtering.
 */
export function LoanPipelineDashboard() {
  const query = useLoanPipeline();
  const { error, dataUpdatedAt } = query;
  const outcome = readOutcome(query);
  // Only a successful read produces figures. Every value below is drawn from
  // this, so an unread page has nothing to draw from.
  const data = outcome === 'read' ? query.data : undefined;
  const unread = outcome === 'unread';
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);

  if (outcome === 'failed') {
    return (
      <div className="bg-error/5 border border-error/20 rounded-sm p-4 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
        <div className="text-sm font-sans text-error">
          Failed to load pipeline data: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      </div>
    );
  }

  if (outcome === 'loading') {
    return (
      <div>
        <MetricCardGrid className="mb-8">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-sm" />
          ))}
        </MetricCardGrid>
        <Skeleton className="h-80 rounded-sm mb-4" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Skeleton className="h-72 rounded-sm" />
          <Skeleton className="h-72 rounded-sm" />
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Page Heading */}
      <div className="mb-6">
        {/* Page title lives in the sticky topnav (AppHeader); no redundant H1. */}
        <p className="text-sm font-sans text-tertiary">Application flow, approval rates, and origination volume</p>
      </div>

      {query.isError && data !== undefined && (
        <div className="mb-6" role="status">
          <Alert type="warning" title="Loan pipeline refresh failed">
            <div className="text-xs">
              {`The latest refresh of the loan pipeline failed${error instanceof Error ? ` (${error.message})` : ''}. The figures shown are from the read at ${readTime(dataUpdatedAt)}.`}
            </div>
          </Alert>
        </div>
      )}

      {/* Filter Bar */}
      <FilterBar
        filters={FILTER_DEFINITIONS}
        activeFilters={activeFilters}
        onFiltersChange={setActiveFilters}
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search pipeline by borrower, loan ID..."
        className="mb-6 rounded-sm"
      />

      {/* Key Metrics */}
      <MetricCardGrid className="mb-8">
        <MetricCard
          label="Applications (24h)"
          value={unread ? '--' : String(data?.kpis.applications24h ?? '-')}
          caption={unread ? METRIC_READ_CAPTIONS.unread : undefined}
        />
        <MetricCard
          label="Approval Rate"
          value={unread ? '--' : String(data?.kpis.approvalRate ?? '-')}
          caption={unread ? METRIC_READ_CAPTIONS.unread : undefined}
        />
        <MetricCard
          label="Avg Time to Funding"
          value={unread ? '--' : String(data?.kpis.avgTimeToFunding ?? '-')}
          caption={unread ? METRIC_READ_CAPTIONS.unread : undefined}
        />
        <MetricCard
          label="Total Originated"
          value={unread ? '--' : String(data?.kpis.totalOriginated ?? '-')}
          caption={unread ? METRIC_READ_CAPTIONS.unread : undefined}
        />
      </MetricCardGrid>

      {/* Charts. An unread page draws no chart: an empty series or a donut
          summing to zero would read as a measured empty pipeline. */}
      <div className="grid grid-cols-1 gap-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <div className="lg:col-span-8">
            {unread ? (
              <UnreadChart title="Application Volume (Last 7 Days)" subject="Application volume" />
            ) : (
              <AreaChart
                title="Application Volume (Last 7 Days)"
                data={data?.applicationVolume ?? []}
                dataKey="applications"
                xAxisKey="date"
                color="rgb(var(--color-growth))"
                height={300}
              />
            )}
          </div>
          <div className="lg:col-span-4">
            {unread ? (
              <UnreadChart title="Pipeline Stages" subject="Pipeline stages" />
            ) : (
              <DonutChart
                title="Pipeline Stages"
                data={(data?.pipelineStages ?? []).map((s) => ({ label: s.name, value: s.value }))}
                size={220}
                thickness={40}
                centerLabel="Total"
                centerValue={(data?.pipelineStages ?? []).reduce((sum, s) => sum + s.value, 0).toLocaleString()}
              />
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {unread ? (
            <UnreadChart title="Approval Rates by Integration" subject="Approval rates" />
          ) : (
            <GroupedBarChart
              title="Approval Rates by Integration"
              data={data?.approvalRates ?? []}
              dataKeys={[
                { key: 'approved', name: 'Approved', color: 'rgb(var(--color-growth))' },
                { key: 'rejected', name: 'Rejected', color: 'rgb(var(--color-accent))' },
              ]}
              xAxisKey="integration"
              height={300}
            />
          )}
          {unread ? (
            <UnreadChart title="Origination Volume (Last 3 Months)" subject="Origination volume" />
          ) : (
            <GroupedBarChart
              title="Origination Volume (Last 3 Months)"
              data={data?.originationVolume ?? []}
              dataKeys={[{ key: 'volume', name: 'Volume ($M)', color: 'rgb(var(--color-growth))' }]}
              xAxisKey="month"
              height={300}
            />
          )}
        </div>
      </div>
    </div>
  );
}
